import rulesets from '@deque/accessibility-standards';
import type { v2 } from '@deque/billing-service-client';
import { Button, Offscreen, TextField } from '@deque/cauldron-react';
import {
  SettingsSchemaGeneralAccessibilityStandardValueEnum as AccessibilityStandard,
  type SettingsSchemaGeneralAdvancedRulesValueEnum as AdvancedRules,
  type SettingsSchemaGeneralAxeCoreVersionValueEnum as AxeCoreVersion,
  type SettingsSchemaGeneralExpiredProductVisibilityValueEnum as ExpiredProductVisibility,
  type SettingsSchemaGeneralNeedsReviewValueEnum as NeedsReview,
  SettingTypeEnum,
  type SettingsSchemaGeneralSharedReportAccessControlValueEnum as SharedReportAccessControl
} from '@deque/orgwide-settings-client';
import type { TFunction } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
  type Control,
  Controller,
  useForm,
  useFormState,
  useWatch
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import semverGte from 'semver/functions/gte';
import type { EventData } from '~/common/AxeAccountAnalytics';
import PageTitle from '~/common/components/PageTitle';
import { ProductSlugs } from '~/common/constants';
import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import { useAuthContext } from '~/common/contexts/auth';
import { useLoadedConfiguration } from '~/common/contexts/Configuration';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { useProducts } from '~/common/contexts/products';
import { useServerInfo } from '~/common/contexts/serverInfo';
import { axeCoreVersions } from '~/common/utils/axeCoreVersions';
import {
  getOneOfSettingType,
  getSettingType
} from '~/common/utils/configuration';
import {
  MINIMUM_EN_301_AXE_CORE_VERSION,
  MINIMUM_RGAAv4_AXE_CORE_VERSION,
  MINIMUM_TT_V5_AXE_CORE_VERSION,
  recommendedStandard
} from '~/common/utils/rulesets';
import useTeams from '../../hooks/useTeams';
import styles from './Configuration.css';
import EmailsSetting from './EmailsSetting';
import Form from './Form';
import MultiSelectSetting from './MultiSelectSetting';
import RadioSetting, { type RadioItemWithValueOfType } from './RadioSetting';
import SelectOneOfSetting from './SelectOneOfSetting';
import SelectSetting, { type OptionWithValueOfType } from './SelectSetting';

// Ideally, we would import the type from the settings client, but it appears to
// re-use the same types by default (e.g. issueScreenshotSharing is of type ML).
// To avoid this, we would likely have to declare a separate schema for each one.
export type EnabledDisabled = 'enabled' | 'disabled';

export interface GlobalConfigurationForm {
  accessibilityStandard: AccessibilityStandard;
  accessibilityStandardOptions: AccessibilityStandard[];
  allowUsersToChangeAccessibilityStandard: boolean;
  axeCoreVersion: AxeCoreVersion;
  axeCoreVersionOptions: AxeCoreVersion[];
  allowUsersToChangeAxeCoreVersion: boolean;
  needsReview: NeedsReview;
  allowUsersToChangeNeedsReview: boolean;
  bestPractices: EnabledDisabled;
  allowUsersToChangeBestPractices: boolean;
  experimentalRules: EnabledDisabled;
  allowUsersToChangeExperimentalRules: boolean;
  advancedRules: AdvancedRules;
  allowUsersToChangeAdvancedRules: boolean;
  issueScreenshotSharing: EnabledDisabled;
  allowUsersToChangeIssueScreenshotSharing: boolean;
  ml: EnabledDisabled;
  aiDataSharing: EnabledDisabled;
  disabledAiDataSharing: 'disabled'; // This is used to disable the AI data sharing field when ML is disabled
  allowUsersToChangeMl: boolean;
  allowUsersToChangeAiDataSharing: boolean;
  usageService: EnabledDisabled;
  usageServiceUrl: string;
  usageServiceOrganization: string;
  usageServiceDepartment: string;
  usageServiceApplication: string;
  sharedReportAccessControl: SharedReportAccessControl;
  expiredProducts: ExpiredProductVisibility;
  notifyUsers: string[];
  notifyTeams: string[];
}

const focusableFields: (keyof GlobalConfigurationForm)[] = ['expiredProducts'];

/* The axeMonitor is temporarily removed from settingsAppliedProducts.
 * TODO: add it back after the axeMonitor improvements are completed.
 */
export const settingsAppliedProducts = {
  accessibilityStandard: [
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeDevToolsWatcher
    // ProductSlugs.axeMonitor
  ],
  defaultAccessibilityStandard: [
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeDevToolsWatcher
    // ProductSlugs.axeMonitor
  ],
  axeCoreVersion: [
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeDevToolsWatcher
    // ProductSlugs.axeMonitor
  ],
  defaultAxeCoreVersion: [
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeDevToolsWatcher
    // ProductSlugs.axeMonitor
  ],
  defaultNeedsReview: [ProductSlugs.axeDevToolsExtension],
  defaultBestPractices: [
    ProductSlugs.axeDevToolsWatcher,
    ProductSlugs.axeDevToolsExtension
    // ProductSlugs.axeMonitor
  ],
  defaultExperimentalRules: [
    ProductSlugs.axeDevToolsWatcher,
    ProductSlugs.axeDevToolsExtension
  ],
  defaultAdvancedRules: [
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeMCPServer
  ],
  defaultSharedIssueScreenshots: [ProductSlugs.axeDevToolsExtension],
  defaultMachineLearning: [ProductSlugs.axeDevToolsExtension],
  sharedReportAccessControl: [
    ProductSlugs.axeDevToolsWatcher,
    ProductSlugs.axeDevToolsExtension
  ],
  usageService: [
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeDevToolsMobile
  ]
};

type SettingsAppliedProductsKeys = keyof typeof settingsAppliedProducts;
export const axeWatcherNameKeys: SettingsAppliedProductsKeys[] = [
  'accessibilityStandard',
  'defaultAccessibilityStandard',
  'axeCoreVersion',
  'defaultAxeCoreVersion',
  'defaultBestPractices',
  'defaultExperimentalRules'
];

const buildProductNamesForSettings = (
  subscribedProducts: string[],
  getProductBySlug: (slug: string) => v2.Product | undefined
): Record<string, string[] | undefined> => {
  const settingsEntries = Object.entries(settingsAppliedProducts);
  const hasAxeDevToolsHtmlSubscription = subscribedProducts.includes(
    ProductSlugs.axeDevToolsHTML
  );

  const availableProducts = hasAxeDevToolsHtmlSubscription
    ? Array.from(
        new Set([
          ...subscribedProducts,
          ProductSlugs.axeDevToolsWatcher,
          ProductSlugs.axeLinter,
          ProductSlugs.axeDevToolsExtension
        ])
      )
    : subscribedProducts;

  return settingsEntries.reduce<Record<string, string[] | undefined>>(
    (acc, [setting, products]) => {
      const affectedProducts = products.filter(product =>
        availableProducts.includes(product)
      );

      const affectedProductNames = affectedProducts.map(product => {
        const productName = getProductBySlug(product)?.name || '';

        if (product !== ProductSlugs.axeDevToolsWatcher) {
          return productName;
        }

        const shouldShowAxeWatcherName = axeWatcherNameKeys.includes(
          setting as SettingsAppliedProductsKeys
        );
        return shouldShowAxeWatcherName ? 'Axe Watcher' : productName;
      });

      acc[setting] = affectedProducts.length ? affectedProductNames : undefined;
      return acc;
    },
    {}
  );
};

const getSettingDescriptions = (
  subscribedProducts: string[],
  getProductBySlug: (slug: string) => v2.Product | undefined,
  t: TFunction
) => {
  const productNamesMap = buildProductNamesForSettings(
    subscribedProducts,
    getProductBySlug
  );

  return Object.entries(settingsAppliedProducts).reduce<
    Record<string, string | undefined>
  >((acc, [setting]) => {
    const names = productNamesMap[setting];

    if (!names) {
      acc[setting] = undefined;
      return acc;
    }

    const productNames = names.join(', ');
    const isSingleProductAffected = names.length === 1;

    acc[setting] = isSingleProductAffected
      ? t('This setting applies to {{productNames}} only.', { productNames })
      : t('This setting applies to {{productNames}}.', { productNames });

    return acc;
  }, {});
};

const getAffectedProductNameStrings = (
  subscribedProducts: string[],
  getProductBySlug: (slug: string) => v2.Product | undefined
): Record<string, string | undefined> => {
  const productNamesMap = buildProductNamesForSettings(
    subscribedProducts,
    getProductBySlug
  );

  return Object.fromEntries(
    Object.entries(productNamesMap).map(([setting, names]) => [
      setting,
      names?.join(', ')
    ])
  );
};

export type InputsWithAnalytics = Omit<
  Omit<GlobalConfigurationForm, 'disabledAiDataSharing'>,
  | 'usageService'
  | 'usageServiceUrl'
  | 'usageServiceOrganization'
  | 'usageServiceDepartment'
  | 'usageServiceApplication'
>;

export const analyticsProperties: Omit<
  Record<keyof InputsWithAnalytics, string>,
  'disabledAiDataSharing'
> = {
  accessibilityStandard: 'AccessibilityStandardDefault',
  accessibilityStandardOptions: 'AccessibilityStandardOptions',
  allowUsersToChangeAccessibilityStandard: 'AccessibilityStandardAllowChange',
  axeCoreVersion: 'axeCoreVersionDefault',
  axeCoreVersionOptions: 'AxeCoreVersionOptions',
  allowUsersToChangeAxeCoreVersion: 'axeCoreVersionAllowChange',
  needsReview: 'needsReviewDefault',
  allowUsersToChangeNeedsReview: 'needsReviewAllowChange',
  bestPractices: 'bestPracticesDefault',
  allowUsersToChangeBestPractices: 'bestPracticesAllowChange',
  experimentalRules: 'experimentalDefault',
  allowUsersToChangeExperimentalRules: 'experimentalAllowChange',
  advancedRules: 'advancedRulesDefault',
  allowUsersToChangeAdvancedRules: 'advancedRulesAllowChange',
  issueScreenshotSharing: 'shareIssueScreenshotsDefault',
  allowUsersToChangeIssueScreenshotSharing: 'shareIssueScreenshotsAllowChange',
  ml: 'MLDefault',
  aiDataSharing: 'AIDataSharingDefault',
  allowUsersToChangeAiDataSharing: 'AIDataSharingAllowChange',
  allowUsersToChangeMl: 'MLAllowChange',
  sharedReportAccessControl: 'sharedReportAccess',
  expiredProducts: 'showExpiredProducts',
  notifyUsers: 'notifyUsers',
  notifyTeams: 'notifyTeams'
} as const;

const doesSupportStandard = ({
  version,
  minimumVersion
}: {
  version: AxeCoreVersion;
  minimumVersion: AxeCoreVersion;
}) => {
  const adjustedVersion = /^[0-9]+.[0-9]+$/.test(version)
    ? `${version}.${Number.MAX_SAFE_INTEGER}`
    : version;

  return (
    adjustedVersion === 'latest' || semverGte(adjustedVersion, minimumVersion)
  );
};

const partiallySupportedStandards = [
  {
    key: AccessibilityStandard.Ttv5,
    name: rulesets.TTv5.name,
    minimumVersion: MINIMUM_TT_V5_AXE_CORE_VERSION
  },
  {
    key: AccessibilityStandard.En301549,
    name: rulesets['EN-301-549'].name,
    minimumVersion: MINIMUM_EN_301_AXE_CORE_VERSION
  },
  {
    key: AccessibilityStandard.Rgaav4,
    name: rulesets.RGAAv4.name,
    minimumVersion: MINIMUM_RGAAv4_AXE_CORE_VERSION
  }
];

const useAccessibilityStandardOptions = ({
  formControl,
  hasEN301SupportV1,
  hasRGAAv4Support,
  isEnterpriseMember
}: {
  formControl: Control<GlobalConfigurationForm>;
  hasEN301SupportV1: boolean;
  hasRGAAv4Support: boolean;
  isEnterpriseMember: boolean;
}) => {
  const { t } = useTranslation();
  const [
    axeCoreVersion,
    axeCoreVersionOptions,
    allowUsersToChangeAxeCoreVersion
  ] = useWatch({
    control: formControl,
    name: [
      'axeCoreVersion',
      'axeCoreVersionOptions',
      'allowUsersToChangeAxeCoreVersion'
    ]
  });
  const availableAccessibilityStandardOptions =
    useMemo((): OptionWithValueOfType<AccessibilityStandard> => {
      const accessibilityStandardOptions: OptionWithValueOfType<AccessibilityStandard> =
        [];
      Object.entries(rulesets).map(([key, ruleset]) => {
        let disabled = false;
        let disableSelectOptionOnly = false;

        // Don't disable until the user has selected a version
        const partiallySupportedData = partiallySupportedStandards.find(
          standard => standard.key === key
        );
        if (axeCoreVersion && axeCoreVersionOptions && partiallySupportedData) {
          // Disable the partially supported standards if the Axe-core version is
          // not compatible or the available Axe-core versions don't support standard
          const optionsSupportStandard = axeCoreVersionOptions.some(version =>
            doesSupportStandard({
              version,
              minimumVersion: partiallySupportedData.minimumVersion
            })
          );
          disabled =
            !doesSupportStandard({
              version: axeCoreVersion,
              minimumVersion: partiallySupportedData.minimumVersion
            }) || !optionsSupportStandard;
          /* Only disable partially supported standards as default accessibility standard if it is unsupported by the available axe core versions */
          disableSelectOptionOnly =
            isEnterpriseMember &&
            allowUsersToChangeAxeCoreVersion &&
            optionsSupportStandard;
        }

        const rulesetName = partiallySupportedData
          ? t('{{ name }} (requires Axe-core v{{minimum}} or higher)', {
              name: partiallySupportedData.name,
              minimum: partiallySupportedData.minimumVersion
            })
          : ruleset.name;

        if (
          (key !== AccessibilityStandard.En301549 || hasEN301SupportV1) &&
          (key !== AccessibilityStandard.Rgaav4 || hasRGAAv4Support)
        ) {
          accessibilityStandardOptions.push({
            key,
            label:
              key === recommendedStandard
                ? t('{{ rulesetName }} (Recommended)', {
                    rulesetName
                  })
                : rulesetName,
            value: key as AccessibilityStandard,
            disabled,
            disableSelectOptionOnly
          });
        }
      });
      return accessibilityStandardOptions;
    }, [
      t,
      axeCoreVersion,
      axeCoreVersionOptions,
      allowUsersToChangeAxeCoreVersion,
      hasEN301SupportV1,
      hasRGAAv4Support
    ]);

  return {
    accessibilityStandardOptions: availableAccessibilityStandardOptions
  };
};

const GlobalConfiguration = () => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const {
    settings,
    updateSettings,
    saving,
    isEnterpriseMember,
    isEnterpriseAdmin
  } = useLoadedConfiguration();
  const generalSettings = settings.general;
  const {
    handleSubmit,
    reset,
    resetField,
    setFocus,
    control,
    formState,
    setValue,
    getValues,
    watch
  } = useForm<GlobalConfigurationForm>();
  const { touchedFields } = useFormState({ control });
  const hasAiDataSharing = useFeatureFlagState('ai_data_sharing_setting_v1');
  const analytics = useAxeAccountAnalytics();
  const history = useHistory();
  const { search } = useLocation();
  const focus = new URLSearchParams(search).get('focus');
  const [axeCoreVersion, accessibilityStandard] = useWatch({
    control,
    name: ['axeCoreVersion', 'accessibilityStandard']
  });
  const { serverInfo } = useServerInfo();
  const { activeEnterprise } = useEnterprises();
  const { user, billingUser } = useAuthContext();
  const hasTeams = useFeatureFlagState('teams_v1');
  const hasReportsTeamsEnabled = useFeatureFlagState('axe_reports_teams_v1');
  const isGlobalConfigUpdateNotificationEnabled = useFeatureFlagState(
    'global_configuration_update_notification'
  );

  const subscriptions =
    activeEnterprise?.subscriptions || billingUser?.subscriptions || [];

  const hasMonitorAccess = subscriptions.some(
    sub =>
      sub.product_slug === ProductSlugs.axeMonitor &&
      (sub as v2.EnterpriseSubscription).instance_url
  );

  const hasReportsAccess = subscriptions.some(
    sub => sub.product_slug === ProductSlugs.axeReports
  );
  const showSelectTeamsToNotify =
    isEnterpriseAdmin &&
    ((hasTeams && hasMonitorAccess) ||
      (hasReportsAccess && hasReportsTeamsEnabled));

  const { teams } = useTeams({
    token: user?.token ?? '',
    enterpriseId: activeEnterprise?.id ?? ''
  });

  const {
    descriptions: affectedProductsBySettingDescriptions,
    names: affectedProductsBySettingNames
  } = useMemo(() => {
    const subscribedProducts = subscriptions.map(sub => sub.product_slug);
    return {
      descriptions: getSettingDescriptions(
        subscribedProducts,
        getProductBySlug,
        t
      ),
      names: getAffectedProductNameStrings(subscribedProducts, getProductBySlug)
    };
  }, [subscriptions, getProductBySlug, t]);

  const focusRefHandler = (el: HTMLDivElement) => {
    if (focus && el) {
      if (focusableFields.includes(focus as keyof GlobalConfigurationForm)) {
        const focusField = el;

        setTimeout(() => {
          focusField?.focus({ preventScroll: true });
          focusField?.scrollIntoView({
            block: 'end'
          });
        }, 0);
      } else {
        history.replace('/configuration');
      }
    }
  };

  useEffect(() => {
    if (generalSettings) {
      // Set the default values for the form
      reset({
        accessibilityStandard: generalSettings.accessibilityStandard.value,
        accessibilityStandardOptions:
          generalSettings.accessibilityStandard.type === SettingTypeEnum.OneOf
            ? generalSettings.accessibilityStandard.options
            : (Object.keys(rulesets) as AccessibilityStandard[]),
        allowUsersToChangeAccessibilityStandard: (
          [SettingTypeEnum.OneOf, SettingTypeEnum.Default] as SettingTypeEnum[]
        ).includes(generalSettings.accessibilityStandard.type),
        axeCoreVersion: generalSettings.axeCoreVersion.value,
        axeCoreVersionOptions:
          generalSettings.axeCoreVersion.type === SettingTypeEnum.OneOf
            ? generalSettings.axeCoreVersion.options
            : axeCoreVersions,
        allowUsersToChangeAxeCoreVersion: (
          [SettingTypeEnum.OneOf, SettingTypeEnum.Default] as SettingTypeEnum[]
        ).includes(generalSettings.axeCoreVersion.type),
        needsReview: generalSettings.needsReview.value,
        allowUsersToChangeNeedsReview:
          generalSettings.needsReview.type === SettingTypeEnum.Default,
        bestPractices: generalSettings.bestPractices.value,
        allowUsersToChangeBestPractices:
          generalSettings.bestPractices.type === SettingTypeEnum.Default,
        experimentalRules: generalSettings.experimentalRules.value,
        allowUsersToChangeExperimentalRules:
          generalSettings.experimentalRules.type === SettingTypeEnum.Default,
        advancedRules: generalSettings.advancedRules?.value ?? 'balanced',
        allowUsersToChangeAdvancedRules:
          generalSettings.advancedRules?.type === SettingTypeEnum.Default,
        issueScreenshotSharing: generalSettings.issueScreenshotSharing.value,
        allowUsersToChangeIssueScreenshotSharing:
          generalSettings.issueScreenshotSharing.type ===
          SettingTypeEnum.Default,
        ml: generalSettings.ml.value,
        allowUsersToChangeMl:
          generalSettings.ml.type === SettingTypeEnum.Default,
        aiDataSharing: generalSettings.aiDataSharing.value,
        disabledAiDataSharing: 'disabled',
        allowUsersToChangeAiDataSharing:
          generalSettings.aiDataSharing.type === SettingTypeEnum.Default,
        sharedReportAccessControl:
          generalSettings.sharedReportAccessControl.value,
        usageService: generalSettings.usageService.value.status,
        usageServiceUrl: generalSettings.usageService.value.url || '',
        usageServiceOrganization:
          generalSettings.usageService.value.organization || '',
        usageServiceDepartment:
          generalSettings.usageService.value.department || '',
        usageServiceApplication:
          generalSettings.usageService.value.application || '',
        expiredProducts: generalSettings.expiredProductVisibility.value,
        notifyUsers: generalSettings.notifyUsers.value || [],
        notifyTeams: generalSettings.notifyTeams.value || []
      });
    }
  }, [generalSettings]);

  const onSubmit = (data: GlobalConfigurationForm) => {
    updateSettings({
      general: {
        accessibilityStandard: {
          value: data.accessibilityStandard,
          type: isEnterpriseAdmin
            ? getOneOfSettingType(data.allowUsersToChangeAccessibilityStandard)
            : generalSettings.accessibilityStandard.type,
          options:
            getOneOfSettingType(
              data.allowUsersToChangeAccessibilityStandard
            ) === SettingTypeEnum.OneOf
              ? data.accessibilityStandardOptions
              : undefined
        },
        axeCoreVersion: {
          value: data.axeCoreVersion,
          type: isEnterpriseAdmin
            ? getOneOfSettingType(data.allowUsersToChangeAxeCoreVersion)
            : generalSettings.axeCoreVersion.type,
          options:
            getOneOfSettingType(data.allowUsersToChangeAxeCoreVersion) ===
            SettingTypeEnum.OneOf
              ? data.axeCoreVersionOptions
              : undefined
        },
        needsReview: {
          value: data.needsReview,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeNeedsReview)
            : generalSettings.needsReview.type
        },
        bestPractices: {
          value: data.bestPractices,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeBestPractices)
            : generalSettings.bestPractices.type
        },
        experimentalRules: {
          value: data.experimentalRules,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeExperimentalRules)
            : generalSettings.experimentalRules.type
        },
        advancedRules: {
          value: data.advancedRules,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeAdvancedRules)
            : (generalSettings.advancedRules?.type ?? SettingTypeEnum.Default)
        },
        issueScreenshotSharing: {
          value: data.issueScreenshotSharing,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeIssueScreenshotSharing)
            : generalSettings.issueScreenshotSharing.type
        },
        ml: {
          value: data.ml,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeMl)
            : generalSettings.ml.type
        },
        aiDataSharing: {
          value: data.aiDataSharing,
          type: isEnterpriseAdmin
            ? getSettingType(data.allowUsersToChangeAiDataSharing)
            : generalSettings.aiDataSharing.type
        },
        sharedReportAccessControl: {
          value: data.sharedReportAccessControl,
          type: generalSettings.sharedReportAccessControl.type
        },
        usageService: {
          value: {
            status: data.usageService,
            ...(data.usageService === 'enabled'
              ? {
                  url:
                    data.usageServiceUrl ||
                    generalSettings.usageService.value.url,
                  organization: data.usageServiceOrganization || null,
                  department: data.usageServiceDepartment || null,
                  application: data.usageServiceApplication || null
                }
              : {})
          },
          type: generalSettings.usageService.type
        },
        expiredProductVisibility: {
          value: data.expiredProducts,
          type: generalSettings.expiredProductVisibility.type
        },
        notifyUsers: {
          value: data.notifyUsers,
          type: generalSettings.notifyUsers.type
        },
        notifyTeams: {
          value: data.notifyTeams,
          type: generalSettings.notifyTeams.type
        }
      }
    });

    const changedProperties = Object.keys(
      formState.dirtyFields as Partial<GlobalConfigurationForm>
    ).reduce<EventData>((acc, key) => {
      const field = key as keyof InputsWithAnalytics;
      if (field === 'expiredProducts') {
        acc[analyticsProperties[field]] = data[field] === 'all' ? true : false;
      } else if (field in analyticsProperties) {
        acc[analyticsProperties[field]] = Array.isArray(data[field])
          ? (data[field] as unknown[]).join(',')
          : (data[field] as string);
      }

      return acc;
    }, {});

    analytics.configSubmit('global', changedProperties);
  };

  useEffect(() => {
    analytics.viewConfigurationGlobalPage(isEnterpriseAdmin);
  }, []);

  const isUsageServiceEnabled = watch('usageService') === 'enabled';
  const isAllowUsersToChangeAccessibilityStandard =
    watch('allowUsersToChangeAccessibilityStandard') && isEnterpriseAdmin;
  const isAllowUsersToChangeAxeCoreVersion =
    watch('allowUsersToChangeAxeCoreVersion') && isEnterpriseAdmin;
  const isReadOnly = isEnterpriseMember && !isEnterpriseAdmin;
  const disabled = isReadOnly || saving;
  const hasEN301SupportV1 = useFeatureFlagState('en_301_549_v1');
  const hasRGAAv4Support = useFeatureFlagState('rgaa_v4_support');
  const hasRetroactiveAccessControl = useFeatureFlagState(
    'retroactive_access_control'
  );

  const { accessibilityStandardOptions } = useAccessibilityStandardOptions({
    formControl: control,
    hasEN301SupportV1,
    hasRGAAv4Support,
    isEnterpriseMember
  });

  useEffect(() => {
    partiallySupportedStandards.forEach(partiallySupportedData => {
      const partiallySupportedOption = accessibilityStandardOptions.find(
        option => option.key === partiallySupportedData.key
      );

      // If partially supported option is disabled as a oneOf option, we want to make sure it gets unselected
      if (
        partiallySupportedOption?.disabled &&
        !partiallySupportedOption?.disableSelectOptionOnly
      ) {
        setValue(
          'accessibilityStandardOptions',
          getValues('accessibilityStandardOptions').filter(
            value => value !== partiallySupportedOption.value
          )
        );
      }
    });
  }, [accessibilityStandardOptions]);

  useEffect(() => {
    const partiallySupportedData = partiallySupportedStandards.find(
      standard => accessibilityStandard === standard.key
    );
    if (
      partiallySupportedData &&
      !doesSupportStandard({
        version: axeCoreVersion,
        minimumVersion: partiallySupportedData.minimumVersion
      })
    ) {
      setValue('accessibilityStandard', recommendedStandard);
    }
  }, [axeCoreVersion, accessibilityStandard]);

  const axeCoreVersionOptions = useMemo(
    (): OptionWithValueOfType<AxeCoreVersion> =>
      axeCoreVersions.map(version => ({
        key: version,
        label: version === 'latest' ? t('Latest (4.x, Recommended)') : version,
        value: version
      })),
    [t]
  );

  const needsReviewOptions = useMemo(
    (): OptionWithValueOfType<NeedsReview> => [
      {
        key: 'enabled',
        label: t('Enable needs review issues and include in auto issue total'),
        value: 'enabled'
      },
      {
        key: 'excluded',
        label: t(
          'Enable needs review issues but exclude from auto issue total'
        ),
        value: 'excluded'
      },
      {
        key: 'disabled',
        label: t('Disabled'),
        value: 'disabled'
      }
    ],
    [t]
  );

  const enabledDisabledOptions = useMemo(
    (): RadioItemWithValueOfType<EnabledDisabled> => [
      {
        label: t('Enable'),
        value: 'enabled'
      },
      {
        label: t('Disable'),
        value: 'disabled'
      }
    ],
    [t]
  );

  const advancedRulesOptions = useMemo(
    (): RadioItemWithValueOfType<AdvancedRules> => [
      {
        label: t('Precise (90%+ confidence)'),
        value: 'precise'
      },
      {
        label: t('Balanced (70%+ confidence)'),
        value: 'balanced'
      },
      {
        label: t('Thorough (50%+ confidence)'),
        value: 'thorough'
      },
      {
        label: t('Disabled'),
        value: 'disabled'
      }
    ],
    [t]
  );

  const sharedReportAccessControlOptions = useMemo(
    (): OptionWithValueOfType<SharedReportAccessControl> => [
      {
        key: 'anyone',
        label: t('Anyone with the link'),
        value: 'anyone'
      },
      {
        key: 'users',
        label: t('Any logged in user'),
        value: 'users'
      },
      ...(isEnterpriseMember
        ? ([
            {
              key: 'enterprise',
              label: t('Any logged in enterprise user'),
              value: 'enterprise'
            }
          ] as OptionWithValueOfType<SharedReportAccessControl>)
        : [])
    ],
    [t, isEnterpriseMember]
  );

  const expiredProductsOptions = useMemo(
    (): RadioItemWithValueOfType<ExpiredProductVisibility> => [
      {
        label: t('Show all expired products'),
        value: 'all'
      },
      {
        label: t('Hide all expired products'),
        value: 'none'
      }
    ],
    [t]
  );

  const mlSettingDisabled = watch('ml') === 'disabled';
  const resetUsageServiceURL = () => {
    resetField('usageServiceUrl');
    setFocus('usageServiceUrl');
  };

  const onDiscard = () => {
    analytics.configDiscard('global');
    reset();
  };

  const [aiDataSharingStatusText, setAiDataSharingStatusText] =
    useState<string>('');
  useEffect(() => {
    const aiDataSharingEnabled = getValues('aiDataSharing') === 'enabled';
    if (mlSettingDisabled && aiDataSharingEnabled && touchedFields.ml) {
      // on disable ai powered features
      setAiDataSharingStatusText(
        t(
          `Default AI Data Sharing has been automatically disabled due to a change of the "Default AI-Powered Features" setting.`
        )
      );
    } else if (!mlSettingDisabled && aiDataSharingEnabled && touchedFields.ml) {
      // on enable ai powered features
      setAiDataSharingStatusText(
        t(
          `Default AI Data Sharing has been automatically enabled due to a change of the "Default AI-Powered Features" setting.`
        )
      );
    }
  }, [mlSettingDisabled, touchedFields.ml]);

  return (
    <>
      <PageTitle title={t('Configuration - Global')} />
      <Form
        header={t('Global')}
        isDirty={formState.isDirty}
        isReadOnly={isReadOnly}
        onSave={handleSubmit(onSubmit)}
        onDiscard={onDiscard}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <h3>{t('Rules and Issues Settings')}</h3>

          {isEnterpriseAdmin ? (
            <SelectOneOfSetting
              id="accessibility-standard"
              name="accessibilityStandard"
              allowChangeName="allowUsersToChangeAccessibilityStandard"
              allowChangeProductNames={
                affectedProductsBySettingNames.accessibilityStandard
              }
              shouldShowAllowChange={isEnterpriseMember}
              groupLabel={t('Accessibility Standard')}
              groupDescription={t(
                `Choose the default accessibility standard for your organization. {{additionalDescription}}`,
                {
                  additionalDescription:
                    affectedProductsBySettingDescriptions.accessibilityStandard
                }
              )}
              defaultLabel={t('Default Accessibility Standard')}
              tooltip={t(
                'WCAG has three levels of conformance: A, AA, and AAA. Level A refers to the lowest level of conformance (minimum) and Level AAA is the highest (maximum). Trusted Tester v5 is also an option, which is a separate conformance testing process.'
              )}
              selectDescription={
                affectedProductsBySettingDescriptions.defaultAccessibilityStandard
              }
              options={accessibilityStandardOptions}
              optionsName="accessibilityStandardOptions"
              showOneOfOptions={isAllowUsersToChangeAccessibilityStandard}
              formControl={control}
              disabled={disabled}
            />
          ) : (
            <SelectSetting
              id="default-wcag-level"
              name="accessibilityStandard"
              allowChangeName="allowUsersToChangeAccessibilityStandard"
              allowChangeProductNames={
                affectedProductsBySettingNames.accessibilityStandard
              }
              shouldShowAllowChange={isEnterpriseMember}
              label={t('Default Accessibility Standard')}
              tooltip={t(
                'WCAG has three levels of conformance: A, AA, and AAA. Level A refers to the lowest level of conformance (minimum) and Level AAA is the highest (maximum). Trusted Tester v5 is also an option, which is a separate conformance testing process.'
              )}
              selectDescription={
                affectedProductsBySettingDescriptions.defaultAccessibilityStandard
              }
              options={accessibilityStandardOptions}
              formControl={control}
              disabled={disabled}
            />
          )}

          {isEnterpriseAdmin ? (
            <SelectOneOfSetting
              id="axe-version"
              name="axeCoreVersion"
              allowChangeName="allowUsersToChangeAxeCoreVersion"
              allowChangeProductNames={
                affectedProductsBySettingNames.axeCoreVersion
              }
              shouldShowAllowChange={isEnterpriseMember}
              groupLabel={t('Axe-core Version')}
              groupDescription={t(
                `Choose the default Axe-core version for your organization. {{additionalDescription}}`,
                {
                  additionalDescription:
                    affectedProductsBySettingDescriptions.axeCoreVersion
                }
              )}
              defaultLabel={t('Default Axe-core Version')}
              selectDescription={
                affectedProductsBySettingDescriptions.defaultAxeCoreVersion
              }
              options={axeCoreVersionOptions}
              optionsName="axeCoreVersionOptions"
              showOneOfOptions={isAllowUsersToChangeAxeCoreVersion}
              formControl={control}
              disabled={disabled}
            />
          ) : (
            <SelectSetting
              id="default-axe-core-version"
              name="axeCoreVersion"
              allowChangeName="allowUsersToChangeAxeCoreVersion"
              allowChangeProductNames={
                affectedProductsBySettingNames.axeCoreVersion
              }
              shouldShowAllowChange={isEnterpriseMember}
              label={t('Default Axe-core Version')}
              tooltip={t(
                'Selecting latest will ensure you are always using the version of Axe-core with the most rules and latest fixes.'
              )}
              selectDescription={
                affectedProductsBySettingDescriptions.defaultAxeCoreVersion
              }
              options={axeCoreVersionOptions}
              formControl={control}
              disabled={disabled}
            />
          )}

          <SelectSetting
            id="default-needs-review"
            name="needsReview"
            allowChangeName="allowUsersToChangeNeedsReview"
            shouldShowAllowChange={isEnterpriseMember}
            label={t('Default Needs Review')}
            selectDescription={
              affectedProductsBySettingDescriptions.defaultNeedsReview
            }
            options={needsReviewOptions}
            formControl={control}
            disabled={disabled}
          />

          <RadioSetting
            id="default-best-practices"
            name="bestPractices"
            allowChangeName="allowUsersToChangeBestPractices"
            allowChangeProductNames={
              affectedProductsBySettingNames.defaultBestPractices
            }
            shouldShowAllowChange={isEnterpriseMember}
            label={t('Default Best Practices')}
            description={t(
              'Enable best practice rules that improve user experience. These rules are industry accepted but do not map to WCAG success criteria. {{additionalDescription}}',
              {
                additionalDescription:
                  affectedProductsBySettingDescriptions.defaultBestPractices
              }
            )}
            options={enabledDisabledOptions}
            formControl={control}
            disabled={disabled}
          />

          <RadioSetting
            id="default-experimental-rules"
            name="experimentalRules"
            allowChangeName="allowUsersToChangeExperimentalRules"
            allowChangeProductNames={
              affectedProductsBySettingNames.defaultExperimentalRules
            }
            shouldShowAllowChange={isEnterpriseMember}
            label={t('Default Experimental Rules')}
            description={t(
              'Enable experimental rules that Deque is still testing. These rules fall outside of Axe-core’s standard ruleset. {{additionalDescription}}',
              {
                additionalDescription:
                  affectedProductsBySettingDescriptions.defaultExperimentalRules
              }
            )}
            options={enabledDisabledOptions}
            formControl={control}
            disabled={disabled}
          />

          <RadioSetting
            id="default-advanced-rules"
            name="advancedRules"
            allowChangeName="allowUsersToChangeAdvancedRules"
            allowChangeProductNames={
              affectedProductsBySettingNames.defaultAdvancedRules
            }
            shouldShowAllowChange={isEnterpriseMember}
            label={t('Default Advanced Rules')}
            description={t(
              'Choose the default confidence threshold for advanced rules. {{additionalDescription}}',
              {
                additionalDescription:
                  affectedProductsBySettingDescriptions.defaultAdvancedRules
              }
            )}
            options={advancedRulesOptions}
            formControl={control}
            disabled={disabled}
          />

          {serverInfo?.screenshotsEnabled && (
            <RadioSetting
              id="default-issue-screenshot-sharing"
              name="issueScreenshotSharing"
              allowChangeName="allowUsersToChangeIssueScreenshotSharing"
              shouldShowAllowChange={isEnterpriseMember}
              label={t('Default Shared Issue Screenshots')}
              description={t(
                'Allow screenshots to be added to shared issues. {{additionalDescription}}',
                {
                  additionalDescription:
                    affectedProductsBySettingDescriptions.defaultSharedIssueScreenshots
                }
              )}
              options={enabledDisabledOptions}
              formControl={control}
              disabled={disabled}
            />
          )}

          {serverInfo?.mlServiceEnabled && (
            <>
              <RadioSetting
                id="default-ml"
                name="ml"
                allowChangeName="allowUsersToChangeMl"
                shouldShowAllowChange={isEnterpriseMember}
                label={
                  hasAiDataSharing
                    ? t('Default AI-Powered Features')
                    : t('Default Machine Learning')
                }
                description={t(
                  'Some Deque products use machine learning to improve accessibility testing. {{additionalDescription}}',
                  {
                    additionalDescription:
                      affectedProductsBySettingDescriptions.defaultMachineLearning
                  }
                )}
                options={enabledDisabledOptions}
                formControl={control}
                disabled={disabled}
              />
              {hasAiDataSharing && (
                <>
                  <Offscreen aria-live="polite" aria-atomic="true">
                    {aiDataSharingStatusText}
                  </Offscreen>
                  {mlSettingDisabled ? (
                    // If ML is disabled, we disable the AI data sharing setting input with a new component so we can save the previous value
                    <RadioSetting
                      key="default-ai-data-sharing-disabled"
                      id="default-ai-data-sharing-disabled"
                      name="disabledAiDataSharing"
                      allowChangeName="allowUsersToChangeAiDataSharing"
                      shouldShowAllowChange={isEnterpriseMember}
                      label={t('Default AI Data Sharing')}
                      tooltip={t(
                        'Enable AI-Powered Features to change this setting'
                      )}
                      options={enabledDisabledOptions}
                      formControl={control}
                      disabled
                    />
                  ) : (
                    <RadioSetting
                      key="default-ai-data-sharing"
                      id="default-ai-data-sharing"
                      name="aiDataSharing"
                      allowChangeName="allowUsersToChangeAiDataSharing"
                      shouldShowAllowChange={isEnterpriseMember}
                      label={t('Default AI Data Sharing')}
                      tooltip={t(
                        'If enabled, your test data including screenshots will be used to train and improve our AI. Any data captured when this setting is enabled, can be used for training. Any data captured when this setting is disabled will not be used for training.'
                      )}
                      options={enabledDisabledOptions}
                      formControl={control}
                      disabled={disabled}
                    />
                  )}
                </>
              )}
            </>
          )}

          <h3>{t('Data Management')}</h3>

          <SelectSetting
            id="shared-report-access-control"
            name="sharedReportAccessControl"
            shouldShowAllowChange={false}
            label={t('Shared Report Access Control')}
            tooltip={
              hasRetroactiveAccessControl
                ? t(
                    'Controls who can access shared reports like Shared Issues and Shared Saved Test Records. This setting will apply to all existing and future reports.'
                  )
                : t(
                    'Controls who can access shared reports like Shared Issues and Shared Saved Test Records.'
                  )
            }
            selectDescription={t(
              'Control who can access shared reports like shared issues and saved test records. {{additionalDescription}}',
              {
                additionalDescription:
                  affectedProductsBySettingDescriptions.sharedReportAccessControl
              }
            )}
            options={sharedReportAccessControlOptions}
            formControl={control}
            disabled={disabled}
          />

          <RadioSetting
            id="usage-service"
            name="usageService"
            shouldShowAllowChange={false}
            label={t('Usage Service')}
            options={enabledDisabledOptions}
            description={affectedProductsBySettingDescriptions.usageService}
            formControl={control}
            disabled={disabled}
          >
            {isUsageServiceEnabled && (
              <div className={styles.textFieldGroup}>
                <Controller
                  control={control}
                  name="usageServiceUrl"
                  rules={{
                    required: true,
                    validate: value => {
                      try {
                        new URL(value);
                        return true;
                      } catch {
                        return false;
                      }
                    }
                  }}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      fieldRef={ref}
                      id="usage-service-url"
                      aria-label={t('Usage Service URL')}
                      label={t('URL')}
                      required
                      requiredText={t('Required')}
                      disabled={disabled}
                      error={
                        formState.errors.usageServiceUrl ? (
                          <div className={styles.errorWithReset}>
                            <Trans>
                              <span>A valid URL is required.</span>
                              <Button
                                id="reset-usage-service-url"
                                aria-label={t('Reset Usage Service URL')}
                                variant="link"
                                onClick={resetUsageServiceURL}
                              >
                                Reset
                              </Button>
                            </Trans>
                          </div>
                        ) : undefined
                      }
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="usageServiceOrganization"
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      fieldRef={ref}
                      id="usage-service-organization"
                      aria-label={t('Usage Service Organization')}
                      label={t('Organization')}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="usageServiceDepartment"
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      fieldRef={ref}
                      id="usage-service-department"
                      aria-label={t('Usage Service Department')}
                      label={t('Department')}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="usageServiceApplication"
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      fieldRef={ref}
                      id="usage-service-application"
                      aria-label={t('Usage Service Application')}
                      label={t('Application')}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            )}
          </RadioSetting>

          <h3>{t('Billing and Products')}</h3>
          <RadioSetting
            id="expired-products"
            name="expiredProducts"
            shouldShowAllowChange={false}
            label={t('Expired Products')}
            options={expiredProductsOptions}
            formControl={control}
            disabled={disabled}
            focusRef={focusRefHandler}
          />

          {isGlobalConfigUpdateNotificationEnabled && (
            <>
              <h3>{t('Notify Users of Changes')}</h3>
              <EmailsSetting
                id="notify-users"
                name="notifyUsers"
                label={t('Enter email addresses manually (comma separated)')}
                formControl={control}
                disabled={disabled}
              />

              {showSelectTeamsToNotify && (
                <MultiSelectSetting
                  id="notify-teams"
                  name="notifyTeams"
                  label={t('Select teams to notify')}
                  formControl={control}
                  disabled={disabled}
                  options={
                    teams?.map(team => ({
                      key: team.id,
                      label: team.name,
                      value: team.name
                    })) || []
                  }
                />
              )}
            </>
          )}
        </form>
      </Form>
    </>
  );
};

export default GlobalConfiguration;
